import React from "react";
import { X } from "lucide-react";

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 md:mb-8 animate-fade-up">
    <div className="min-w-0">
      <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tighter">{title}</h1>
      {subtitle && <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>}
    </div>
    {action && <div className="flex flex-wrap items-center gap-2 shrink-0">{action}</div>}
  </div>
);

export const Card = ({ children, className = "", ...props }) => (
  <div {...props} className={`bg-card border border-border rounded-xl p-5 ${className}`}>{children}</div>
);

export const Button = ({ children, variant = "primary", className = "", ...props }) => {
  const styles = {
    primary: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline: "border border-border text-foreground hover:bg-secondary",
    ghost: "text-muted-foreground hover:text-foreground hover:bg-secondary",
    danger: "bg-destructive/15 text-destructive hover:bg-destructive/25",
  };
  return (
    <button {...props}
      className={`inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg text-sm font-bold transition-colors disabled:opacity-50 ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
};

export const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/70" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-y-auto animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 h-16 border-b border-border sticky top-0 bg-card z-10">
          <h3 className="font-display text-base sm:text-lg font-bold uppercase tracking-tight truncate pr-2">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="shrink-0 -mr-1 p-2 text-muted-foreground hover:text-foreground"><X size={20} /></button>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
};

export const Input = (props) => (
  <input {...props}
    className={`w-full h-11 px-4 rounded-lg bg-secondary border border-border text-foreground text-base sm:text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 ${props.className || ""}`} />
);

export const Select = ({ children, ...props }) => (
  <select {...props}
    className={`w-full h-11 px-4 rounded-lg bg-secondary border border-border text-foreground text-base sm:text-sm outline-none focus:border-primary ${props.className || ""}`}>
    {children}
  </select>
);

export const Field = ({ label, children }) => (
  <div className="mb-4">
    <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{label}</label>
    {children}
  </div>
);

export const Empty = ({ text }) => (
  <div className="text-center py-16 text-muted-foreground text-sm border border-dashed border-border rounded-xl">{text}</div>
);

export const Badge = ({ children, color = "primary", ...rest }) => {
  const map = {
    primary: "bg-primary/15 text-primary",
    green: "bg-primary/15 text-primary",
    red: "bg-destructive/15 text-destructive",
    yellow: "bg-yellow-500/15 text-yellow-500",
    orange: "bg-orange-500/15 text-orange-400",
    blue: "bg-sky-500/15 text-sky-400",
    purple: "bg-purple-500/15 text-purple-400",
    gray: "bg-secondary text-muted-foreground",
  };
  return <span {...rest} className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide ${map[color]}`}>{children}</span>;
};
